import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Users, FolderKanban, Wrench, TrendingUp, Calendar, Star } from "lucide-react";

/** Admin reporting dashboard — MRR trend, win rate, install times, tech utilisation, NPS. */
export default function AdminReports() {
  const [data, setData] = useState(null);
  const [nps, setNps] = useState(null);

  useEffect(() => {
    api.get("/admin/reports/overview").then((r) => setData(r.data)).catch((e) => console.warn("reports:", e));
    api.get("/admin/nps/summary").then((r) => setNps(r.data)).catch((e) => console.warn("nps:", e));
  }, []);

  if (!data) return <div className="text-sm text-[#475569] py-10 text-center">Loading reports…</div>;
  const h = data.headline;

  return (
    <div data-testid="admin-reports">
      <p className="overline">Insights</p>
      <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-2">Reports</h1>
      <p className="text-sm text-[#475569] mt-1 mb-6">Business health at a glance.</p>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <Tile icon={Users} label="Clients" value={h.clients} testId="rep-clients" />
        <Tile icon={FolderKanban} label="Active projects" value={h.active_projects} testId="rep-active-projects" />
        <Tile icon={Wrench} label="Open work orders" value={h.open_work_orders} testId="rep-wos" />
        <Tile icon={TrendingUp} label="Win rate" value={`${h.win_rate_pct}%`} testId="rep-winrate" />
        <Tile icon={Calendar} label="Avg install" value={`${h.avg_install_days} d`} testId="rep-installdays" />
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mb-5">
        <div className="card-pb p-5" data-testid="rep-mrr">
          <div className="overline mb-3 flex items-center gap-2"><TrendingUp className="w-3.5 h-3.5 text-[#C9A356]" /> Revenue last 12 months</div>
          <div style={{ width: "100%", height: 220, minHeight: 220 }}>
            <ResponsiveContainer>
              <AreaChart data={data.mrr_trend} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                <defs>
                  <linearGradient id="rep-area" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C9A356" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#C9A356" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#94A3B8" }} />
                <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(v) => [`$${v.toLocaleString()}`, "Revenue"]} />
                <Area type="monotone" dataKey="revenue" stroke="#C9A356" strokeWidth={2} fill="url(#rep-area)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-pb p-5" data-testid="rep-nps">
          <div className="flex items-center justify-between mb-3">
            <div className="overline flex items-center gap-2"><Star className="w-3.5 h-3.5 text-[#C9A356]" /> Net Promoter Score</div>
            {nps?.count > 0 && <span className="text-xs text-[#475569]">{nps.count} responses</span>}
          </div>
          {nps?.count > 0 ? (
            <>
              <div className={`font-heading text-6xl font-bold tabular-nums ${
                nps.score >= 50 ? "text-emerald-600" : nps.score >= 0 ? "text-amber-600" : "text-rose-600"
              }`} data-testid="rep-nps-score">{nps.score}</div>
              <div className="text-xs text-[#475569] mt-1">
                {nps.score >= 50 ? "Excellent" : nps.score >= 0 ? "Average" : "Needs attention"}
              </div>
              <div className="flex gap-3 mt-4 text-xs">
                <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-700">Promoters {nps.promoters}</span>
                <span className="px-2 py-1 rounded bg-amber-50 text-amber-700">Passives {nps.passives}</span>
                <span className="px-2 py-1 rounded bg-rose-50 text-rose-700">Detractors {nps.detractors}</span>
              </div>
            </>
          ) : (
            <div className="text-sm text-[#475569]">No responses yet. Survey auto-prompts clients 30 days after install.</div>
          )}
        </div>
      </div>

      <div className="card-pb p-5">
        <div className="overline mb-3">Tech utilisation (last 14 days)</div>
        {data.tech_utilisation.length === 0 ? (
          <div className="text-sm text-[#475569]">No daily-log activity yet.</div>
        ) : (
          <ul className="space-y-2" data-testid="rep-tech-util">
            {data.tech_utilisation.map((t) => (
              <li key={t.employee_id} className="flex items-center gap-3" data-testid={`util-${t.employee_id}`}>
                <span className="w-32 text-sm font-medium truncate">{t.name || t.employee_id}</span>
                <span className="flex-1 h-2.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                  <span className={`block h-full rounded-full ${t.utilisation_pct >= 70 ? "bg-emerald-500" : t.utilisation_pct >= 40 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${t.utilisation_pct}%` }} />
                </span>
                <span className="w-20 text-right text-xs text-[#475569] tabular-nums">{t.logged_hours}h · {t.utilisation_pct}%</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, testId }) {
  return (
    <div className="card-pb p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-[#475569]"><Icon className="w-4 h-4 text-[#C9A356]" /><span className="overline truncate">{label}</span></div>
      <div className="font-heading text-2xl font-bold text-[#0A2540] dark:text-white mt-1 tabular-nums">{value}</div>
    </div>
  );
}
